import { useMemo } from 'react';
import type { ChartRecommendation } from '../../../types';
import { ChartContainer } from './ChartContainer';
import { BarChartView } from './BarChartView';
import { LineChartView } from './LineChartView';
import { transformYearMonthForLine } from './transformYearMonth';
import { DonutChartView } from './DonutChartView';
import { ScatterChartView } from './ScatterChartView';
import { HistogramView } from './HistogramView';
import { BoxPlotView } from './BoxPlotView';
import { AreaVolumeView } from './AreaVolumeView';
import { CandlestickView } from './CandlestickView';
import { DataTableView } from './DataTableView';
import { KpiCardView } from './KpiCardView';

interface EraxChartProps {
  data: Record<string, unknown>[];
  recommendation: ChartRecommendation;
}

/**
 * Routes the ChartRecommendation to the correct chart component.
 */
export function EraxChart({ data, recommendation }: EraxChartProps) {
  // For stacked bar: pivot data by groupBy column to extract unique group keys
  const groupKeys = useMemo(() => {
    if (recommendation.chartType !== 'stacked_bar' || !recommendation.config.groupBy) return [];
    const groupCol = recommendation.config.groupBy;
    const keys = new Set<string>();
    data.forEach((row) => {
      const v = row[groupCol];
      if (v !== null && v !== undefined) keys.add(String(v));
    });
    return Array.from(keys);
  }, [data, recommendation]);

  // Line charts: when data has separate Year + Month, use "Month-Year" (e.g. Jan-2025) as x-axis
  const lineChartDataAndConfig = useMemo(() => {
    if (recommendation.chartType !== 'line' && recommendation.chartType !== 'multi_line') {
      return { data, config: recommendation.config };
    }
    const transformed = transformYearMonthForLine(data, recommendation.config);
    if (transformed) return transformed;
    return { data, config: recommendation.config };
  }, [data, recommendation.chartType, recommendation.config]);

  const chart = (() => {
    switch (recommendation.chartType) {
      case 'bar':
        return <BarChartView data={data} config={recommendation.config} grouped={recommendation.config.grouped} />;
      case 'stacked_bar':
        return (
          <BarChartView
            data={data}
            config={recommendation.config}
            stacked
            groupKeys={groupKeys}
          />
        );
      case 'line':
      case 'multi_line':
        return (
          <LineChartView
            data={lineChartDataAndConfig.data}
            config={lineChartDataAndConfig.config}
          />
        );
      case 'donut':
        return <DonutChartView data={data} config={recommendation.config} />;
      case 'scatter':
        return <ScatterChartView data={data} config={recommendation.config} />;
      case 'bubble':
        return <ScatterChartView data={data} config={recommendation.config} bubble />;
      case 'histogram':
        return <HistogramView data={data} config={recommendation.config} />;
      case 'box_plot':
        return <BoxPlotView data={data} config={recommendation.config} />;
      case 'area_volume':
        return <AreaVolumeView data={data} config={recommendation.config} />;
      case 'candlestick':
        return <CandlestickView data={data} config={recommendation.config} />;
      case 'kpi_card':
        return <KpiCardView data={data} config={recommendation.config} />;
      case 'table':
      default:
        return <DataTableView data={data} />;
    }
  })();

  // Table fallback doesn't need the chart container wrapper
  if (recommendation.chartType === 'table') {
    return <DataTableView data={data} />;
  }

  return (
    <ChartContainer recommendation={recommendation}>
      {chart}
    </ChartContainer>
  );
}
